import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
  Chip,
  MenuItem,
} from "@mui/material";

import DeckIcon from "../../../components/icons/DeckIcon";
import {
  BCPClipboardList,
  BCPSquarePlus,
  BCPTrash,
} from "../../../components/icons/bcp/BCPIcons";

import {
  deleteData,
  getData,
  newPath,
  postData,
} from "../../../components/utilities/UtilityFunctions";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import ArmyListComponent from "./ArmyListComponent";
import { useConfirm } from "material-ui-confirm";
import * as Yup from "yup";
import { Formik } from "formik";
import moment from "moment";
import "moment-timezone";
import { getArmies } from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";

import { getArmyListColor, userDisplayName } from "./EventFunctions";
import OrganizePlayerModal from "./OrganizePlayerModal";
import StyledMenu from "../../../components/layout/StyledMenu";
import NonPlayingMemberModal from "./SubComponents/NonPlayingMemberModal";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

export default function EventTeamDetails(props) {
  const {
    activeTab,
    setActiveTab,
    event,
    setEvent,
    handleRefreshEvent,
    authContext,
  } = props;
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const [player, setPlayer] = useState(null);
  const [captainIsPlayer, setCaptainIsPlayer] = useState(false);
  const [players, setPlayers] = useState(null);
  const [subLoading] = useState(false);
  const [savedPlayer, setSavedPlayer] = useState(null);
  const [playerDict, setPlayerDict] = useState({});
  const [childPlayer, setChildPlayer] = useState(null);

  const [armies, setArmies] = useState(null);
  const [, setArmyDict] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [armyModalOpen, setArmyModalOpen] = useState(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const [nonPlayingMembers, setNonPlayingMembers] = useState(null);
  const [nonPlayingMemberModal, setNonPlayingMemberModal] = useState(false);

  const handleSetPlayer = (newPlayer) => {
    setPlayer({
      ...player,
      ...newPlayer,
    });
    setEvent({
      ...event,
      player: {
        ...event.player,
        ...newPlayer,
      },
    });
  };

  const handlePlayers = async (teamPlayerId) => {
    var apiGetPlayers = await getData(`/players`, {
      queryStringParameters: {
        teamPlayerId: teamPlayerId,
        limit: 100,
        "expand[]": ["army"],
      },
    });

    setPlayers(
      apiGetPlayers?.data?.sort((a, b) => {
        var nameA = userDisplayName(
          a?.firstName || a?.user?.firstName,
          a?.lastName || a?.user?.lastName
        ).toLowerCase();
        var nameB = userDisplayName(
          b?.firstName || b?.user?.firstName,
          b?.lastName || b?.user?.lastName
        ).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      })
    );
  };

  const handleFactions = async (gameSystem) => {
    if (gameSystem?.hasFactions) {
      var apiArmies = await getArmies({
        gameType: gameSystem.code,
        "collections[]": ["subFactions"],
      });
      setArmies(apiArmies);

      var newDict = {};
      if (apiArmies?.length) {
        apiArmies.forEach((army) => {
          var subFactions = [];
          if (army.subFactions?.length) {
            army.subFactions.forEach((subFaction) => {
              subFactions.push({
                value: subFaction.id,
                label: subFaction.name,
              });
            });

            subFactions.sort((a, b) => {
              var labelA = a.label.toLowerCase();
              var labelB = b.label.toLowerCase();
              if (labelA > labelB) return 1;
              if (labelA < labelB) return -1;
              return 0;
            });
          }
          newDict[army.id] = subFactions;
        });
      }
      setArmyDict(newDict);
    } else {
      setArmies(null);
    }
  };

  async function parallel(teamPlayerId, gameSystem) {
    const taskPlayers = handlePlayers(teamPlayerId);
    const taskFactions = handleFactions(gameSystem);

    return {
      players: await taskPlayers,
      factions: await taskFactions,
    };
  }

  useEffect(() => {
    const check = async () => {
      if (!savedPlayer) return;
      var array = [];
      var p = { ...savedPlayer };

      p.army = p.armyId
        ? {
            id: p.armyId,
            name: p.armyName,
          }
        : p.army
          ? p.army
          : p.faction
            ? p.faction
            : null;
      p.faction = p.army;

      p.subFaction = p.subFactionId
        ? {
            id: p.subFactionId,
            name: p.subFactionName,
          }
        : null;

      p.character = p.characterId
        ? {
            id: p.characterId,
            name: p.characterName,
          }
        : null;

      var add = true;
      for (let i = 0; i < players.length; i++) {
        if (players[i].id === p.id) {
          add = false;
          if (!p.deleted) {
            array.push({
              ...players[i],
              ...p,
            });
          }
        } else {
          array.push(players[i]);
        }
      }
      if (add && !p.deleted) {
        array.push(p);
      }

      array.sort((a, b) => {
        var nameA = userDisplayName(
          a?.firstName || a?.user?.firstName,
          a?.lastName || a?.user?.lastName
        ).toLowerCase();
        var nameB = userDisplayName(
          b?.firstName || b?.user?.firstName,
          b?.lastName || b?.user?.lastName
        ).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });

      setPlayers(array);
      if (
        authContext?.authState?.identity?.userId &&
        authContext?.authState?.identity?.userId === p.userId
      )
        await handleRefreshEvent();
      setSavedPlayer(null);
    };
    check();
    // eslint-disable-next-line
  }, [savedPlayer]);

  useEffect(() => {
    var dict = {};
    if (players?.length) {
      for (let p of players) {
        dict[p.id] = p;
        if (p.userId === player?.teamPlayer?.teamCaptainId) {
          setCaptainIsPlayer(true);
        }
      }
    }
    setPlayerDict(dict);
    // eslint-disable-next-line
  }, [players]);

  useEffect(() => {
    const check = async () => {
      try {
        if (!event?.player && !event?.teamPlayer) {
          postNotification(
            "warning",
            "You are not an active player for this event."
          );
          let path = newPath({
            active_tab: "overview",
          });
          setActiveTab(0);
          navigate(path, { replace: false });
          return;
        }

        var apiGetTeamPlayer;
        if (event.teamPlayer?.id) {
          apiGetTeamPlayer = await getData(
            `/teamplayers/${event.teamPlayer?.id}`,
            {}
          );
        } else {
          apiGetTeamPlayer = await getData(`/teamplayers`, {
            queryStringParameters: {
              eventId: event.id,
              playerId: event.player.id,
              limit: 1,
              "expand[]": ["teamCaptain"],
            },
          });
          apiGetTeamPlayer = apiGetTeamPlayer?.data?.[0];
        }
        if (!apiGetTeamPlayer) {
          postNotification(
            "warning",
            "Your player record is not assigned to a team."
          );
          let path = newPath({
            active_tab: "overview",
          });
          setActiveTab(0);
          navigate(path, { replace: false });
          return;
        }

        setPlayer({
          ...event.player,
          teamPlayerId: apiGetTeamPlayer.id,
          teamPlayer: apiGetTeamPlayer,
        });

        setNonPlayingMembers(apiGetTeamPlayer?.nonPlayingMembers || []);

        await parallel(apiGetTeamPlayer.id, event.gameSystem);
      } catch (err) {
        postNotification("error", err);
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [activeTab, event]);

  return (
    <>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          {!loading && player?.teamPlayer && (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4" component="h2">
                      Team Status
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled
                      value={
                        player.teamPlayer.dropped
                          ? "DROPPED"
                          : player.teamPlayer.checkedIn
                            ? "CHECKED IN"
                            : "NOT CHECKED IN"
                      }
                      label="Status"
                      fullWidth
                    />
                  </Grid>

                  {!event.disableCheckin && (
                    <Grid item xs={6} sx={{ display: "flex" }}>
                      <Button
                        variant="contained"
                        fullWidth
                        color={
                          !players?.length
                            ? "error"
                            : event.listsAtCheckin &&
                                players.filter(
                                  (el) => !el?.armyListObjectId && !el?.listId
                                ).length
                              ? "warning"
                              : "success"
                        }
                        disabled={
                          event.ended ||
                          !players?.length ||
                          !Boolean(
                            !player.teamPlayer.dropped &&
                              !player.teamPlayer.checkedIn &&
                              !event.started &&
                              // || !event.currentRound
                              moment().isAfter(
                                moment(event.eventDate).subtract(1, "day")
                              )
                          )
                        }
                        onClick={async (e) => {
                          try {
                            var errors = 0;
                            if (
                              event?.gameSystem?.hasFactions &&
                              players.filter((el) => !el?.factionId).length
                            ) {
                              postNotification(
                                "error",
                                `Please select a ${
                                  event?.gameSystem?.factionLabel || "Faction"
                                } for each player`
                              );
                              errors++;
                            }
                            if (
                              event?.gameSystem?.subFactionRequired &&
                              players.filter((el) => !el?.subFactionId).length
                            ) {
                              postNotification(
                                "error",
                                `Please select a ${
                                  event?.gameSystem?.subFactionLabel ||
                                  "Sub Faction"
                                } for each player`
                              );
                              errors++;
                            }
                            if (
                              event.listsAtCheckin &&
                              players.filter(
                                (el) => !el?.armyListObjectId && !el?.listId
                              ).length
                            ) {
                              postNotification(
                                "error",
                                `This tournament requires players to submit a ${
                                  event?.gameSystem?.listLabel || "list"
                                } prior to check in. Please submit a list for each player`
                              );
                              errors++;
                            }
                            if (errors > 0) {
                              return;
                            }

                            setProcessing(true);

                            var updatePlayer = await postData(
                              `/teamplayers/${player?.teamPlayerId}`,
                              {
                                body: {
                                  set: { checkedIn: true },
                                },
                              }
                            );
                            handleSetPlayer(updatePlayer);
                          } catch (err) {
                            postNotification("error", err);
                          }

                          setProcessing(false);
                        }}
                      >
                        Check In
                      </Button>
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={event.disableCheckin ? 12 : 6}
                    sx={{ display: "flex" }}
                  >
                    <Button
                      variant="contained"
                      fullWidth
                      color="error"
                      disabled={
                        event.ended ||
                        !player?.teamPlayer ||
                        player?.teamPlayer?.dropped
                      }
                      onClick={async (e) => {
                        try {
                          await confirm({
                            title: `Confirm Drop.`,
                            description:
                              "Are you sure you want to drop from this event?",
                            dialogProps: {
                              sx: {
                                zIndex: 99999,
                              },
                            },
                          });
                        } catch (err) {
                          return;
                        }

                        try {
                          setProcessing(true);

                          var updatePlayer = await postData(
                            `/teamplayers/${player?.teamPlayerId}`,
                            {
                              body: {
                                set: {
                                  dropped: true,
                                },
                              },
                            }
                          );
                          handleSetPlayer(updatePlayer);
                        } catch (err) {
                          postNotification("error", err);
                        }

                        setProcessing(false);
                      }}
                    >
                      Drop
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                <Formik
                  initialValues={{
                    name: player?.teamPlayer?.name || "",
                    pin: player?.teamPlayer.hasPin ? "****" : "",
                  }}
                  enableReinitialize
                  validationSchema={Yup.object().shape({
                    name: Yup.string().min(3).max(128).required("Required"),
                    pin: Yup.string().max(255),
                  })}
                  onSubmit={async (values) => {
                    if (!player?.teamPlayer) {
                      return;
                    }
                    try {
                      var error = false;
                      var sanitizedValues = {
                        set: {},
                        unset: {},
                      };

                      if (values.name !== "") {
                        sanitizedValues.set.name = values.name;
                      }

                      if (values.pin !== "" && passwordUpdated) {
                        sanitizedValues.set.pin = values.pin;
                      }

                      if (error) {
                        return;
                      }
                      setProcessing(true);
                      var updatePlayer = await postData(
                        `/teamplayers/${player.teamPlayerId}`,
                        {
                          body: sanitizedValues,
                        }
                      );
                      handleSetPlayer(updatePlayer);
                      postNotification("success", "Team Details updated.");
                    } catch (err) {
                      postNotification("error", err);
                    }
                    setProcessing(false);
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    setFieldValue,
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="h4" component="h2">
                            Team Details
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="name"
                            label="Name"
                            value={values.name}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.name && errors.name)}
                            helperText={touched.name && errors.name}
                          />
                        </Grid>
                        {authContext?.authState?.identity?.userId &&
                          (authContext?.authState?.identity?.userId ===
                            player?.teamPlayer?.teamCaptainId ||
                            nonPlayingMembers?.filter(
                              (el) =>
                                el.userId ===
                                authContext?.authState?.identity?.userId
                            )?.length > 0) &&
                          event.enablePasswords &&
                          !event.passwordlessScoring && (
                            <Grid item xs={12}>
                              <TextField
                                name="pin"
                                label="Captain Self Score Pin"
                                value={values.pin}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                onBlur={(e) => {
                                  handleBlur(e);
                                }}
                                onChange={(e) => {
                                  handleChange(e);
                                  setPasswordUpdated(true);
                                }}
                                onFocus={(e) => {
                                  setFieldValue("pin", "");
                                }}
                                error={Boolean(touched.pin && errors.pin)}
                                helperText={touched.pin && errors.pin}
                                type="password"
                              />
                            </Grid>
                          )}

                        <Grid item xs={12}>
                          <Button
                            color="success"
                            fullWidth
                            variant="contained"
                            type="submit"
                          >
                            Update
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                {!Boolean(players) ? (
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h4" component="h2">
                        Team Roster
                      </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign="center">
                      <CircularProgress size={24} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container>
                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <Typography variant="h4" component="h2">
                        Team Roster
                      </Typography>
                    </Grid>

                    <ListResponsive
                      originalList={subLoading ? null : players}
                      actionButton={[
                        ...(event?.player
                          ? []
                          : [
                              {
                                props: {
                                  variant: "outlined",
                                  color: "primary",
                                  disabled: captainIsPlayer,
                                  onClick: (e) => {
                                    setChildPlayer({
                                      userId:
                                        authContext?.authState?.identity
                                          ?.userId,
                                      email:
                                        authContext?.authState?.identity
                                          ?.email || "",
                                      firstName:
                                        authContext?.authState?.identity
                                          ?.firstName ||
                                        authContext?.authState?.identity
                                          ?.given_name ||
                                        "",
                                      lastName:
                                        authContext?.authState?.identity
                                          ?.lastName ||
                                        authContext?.authState?.identity
                                          ?.family_name ||
                                        "",
                                    });
                                  },
                                  startIcon: <BCPSquarePlus />,
                                },
                                label: "Add Me as Player",
                              },
                            ]),
                        {
                          props: {
                            variant: "outlined",
                            color: "primary",
                            onClick: (e) => {
                              setChildPlayer({});
                            },
                            startIcon: <BCPSquarePlus />,
                          },
                          label: "Add Player",
                        },
                      ]}
                      rowProps={{
                        mapFunction: (row, index) => (
                          <PlayerRow
                            row={row}
                            event={event}
                            setPlayer={setChildPlayer}
                            setOptionsAnchorEl={setOptionsAnchorEl}
                          />
                        ),
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                }}
              >
                {!Boolean(players) ? (
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h4" component="h2">
                        Non Playing Members / Coaches / Assistants
                      </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign="center">
                      <CircularProgress size={24} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container>
                    <Grid item xs={12} sx={{ mb: 2 }}>
                      <Typography variant="h4" component="h2">
                        Non Playing Members / Coaches / Assistants
                      </Typography>
                    </Grid>

                    <ListResponsive
                      originalList={subLoading ? null : nonPlayingMembers || []}
                      actionButton={[
                        {
                          props: {
                            variant: "outlined",
                            color: "primary",
                            onClick: (e) => {
                              setNonPlayingMemberModal(true);
                            },
                            startIcon: <BCPSquarePlus />,
                          },
                          label: "Add Non Playing Member",
                        },
                      ]}
                      rowProps={{
                        mapFunction: (row, index) => (
                          <NonPlayingMemberRow
                            teamPlayer={player?.teamPlayer}
                            row={row}
                            confirm={confirm}
                            setProcessing={setProcessing}
                            setNonPlayingMembers={setNonPlayingMembers}
                          />
                        ),
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              <ArmyListOptionsMenu
                event={event}
                player={childPlayer}
                anchorEl={optionsAnchorEl}
                setAnchorEl={setOptionsAnchorEl}
                setLoading={setLoading}
                setPlayer={setChildPlayer}
                setArmyModalOpen={setArmyModalOpen}
              />

              <OrganizePlayerModal
                teamPlayerId={player?.teamPlayerId}
                player={Boolean(optionsAnchorEl) ? null : childPlayer}
                setPlayer={setChildPlayer}
                playerDict={playerDict}
                event={event}
                armies={armies}
                setRefreshPlayers={(e) => {}}
                setSavedPlayer={setSavedPlayer}
              />
              {armyModalOpen && childPlayer && (
                <ArmyListComponent
                  modal
                  event={event}
                  armies={armies}
                  open={armyModalOpen}
                  setOpen={setArmyModalOpen}
                  player={childPlayer}
                  onClose={() => {
                    setChildPlayer(null);
                  }}
                  setSavedPlayer={setSavedPlayer}
                />
              )}
              {nonPlayingMemberModal && (
                <NonPlayingMemberModal
                  teamPlayer={player?.teamPlayer}
                  open={nonPlayingMemberModal}
                  setOpen={setNonPlayingMemberModal}
                  nonPlayingMembers={nonPlayingMembers}
                  setNonPlayingMembers={setNonPlayingMembers}
                  setProcessing={setProcessing}
                />
              )}
            </>
          )}
        </Grid>
      </Container>
      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}

const NonPlayingMemberRow = (props) => {
  const { teamPlayer, row, confirm, setProcessing, setNonPlayingMembers } =
    props;

  if (!row?.userId) {
    return <></>;
  }
  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: "4px",
        boxShadow: 1,
      }}
    >
      <Grid container>
        <Grid
          item
          sx={{
            flex: 1,
          }}
        >
          <Typography variant="body1">
            {userDisplayName(
              row?.firstName || row?.user?.firstName,
              row?.lastName || row?.user?.lastName
            )}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            textAlign: "right",
            maxWidth: ["100%", "100%", "33.333333%", "25%"],
            minWidth: ["100%", "100%", "33.333333%", "25%"],
          }}
        >
          <IconButton
            onClick={async (e) => {
              try {
                await confirm({
                  title: `Are you sure?`,
                  description: "Are you sure you want to remove this user?",
                  dialogProps: {
                    sx: {
                      zIndex: 99999,
                    },
                  },
                });
              } catch (err) {
                return;
              }
              setProcessing(true);
              try {
                if (teamPlayer?.id) {
                  const apiTeamPlayer = await deleteData(
                    `/teamplayers/${teamPlayer?.id}/nonPlayingMember`,
                    {
                      body: { userId: row?.userId },
                    }
                  );
                  setNonPlayingMembers(apiTeamPlayer?.nonPlayingMembers || []);
                }
              } catch (err) {
                postNotification("error", err);
              } finally {
                setProcessing(false);
              }
            }}
          >
            <BCPTrash />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

const PlayerRow = (props) => {
  const { row, setPlayer, setOptionsAnchorEl } = props;

  if (!row?.userId) {
    return <></>;
  }
  return (
    <Grid
      item
      xs={12}
      onClick={(e) => {
        setPlayer(row);
      }}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: "4px",
        boxShadow: 1,
        "&:hover": {
          cursor: "pointer",
        },
      }}
    >
      <Grid container>
        {getEnvironmentVariable("COMPANY_FLAG") === "solforge" && (
          <Grid item>
            <Avatar
              alt={`${row.user?.firstName} ${row.user?.lastName}`}
              src={row.user?.profileImageUrl}
              sx={{ width: 50, height: 50, mr: 1 }}
            />
          </Grid>
        )}
        <Grid
          item
          sx={{
            flex: 1,
          }}
        >
          <Typography variant="body1">
            {userDisplayName(
              row?.firstName || row?.user?.firstName,
              row?.lastName || row?.user?.lastName
            )}
            {row?.team?.name ? ` - ${row?.team?.name}` : ""}
          </Typography>

          <Typography
            variant="caption"
            paragraph
            sx={{
              mb: 0,
              fontStyle: "italic",
            }}
          >
            {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
              <>
                {row?.armyListSolforge?.myDecks?.length > 0 && (
                  <>
                    {row.armyListSolforge.myDecks
                      .map((deck) => deck.name)
                      .join(" - ")}
                  </>
                )}
              </>
            ) : (
              <>
                {row?.army?.name}
                {row?.character?.name
                  ? ` - ${row?.character.name}`
                  : row?.subFaction?.name
                    ? ` - ${row?.subFaction.name}`
                    : ""}
              </>
            )}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            textAlign: "right",
            maxWidth: ["100%", "100%", "33.333333%", "25%"],
            minWidth: ["100%", "100%", "33.333333%", "25%"],
          }}
        >
          <Chip
            label={
              row?.dropped
                ? "DROPPED"
                : row?.checkedIn
                  ? "CHECKED IN"
                  : "NOT CHECKED IN"
            }
            size="small"
            sx={{ width: "140px" }}
            color={
              row?.dropped ? "error" : row?.checkedIn ? "success" : "warning"
            }
          />

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setOptionsAnchorEl(e.currentTarget);
              setPlayer(row);
            }}
            sx={{
              color: `${getArmyListColor(row)}.main`,
              "&:hover": {
                color: `${getArmyListColor(row)}.dark`,
              },
            }}
          >
            {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
              <DeckIcon color={getArmyListColor(row)} />
            ) : (
              <BCPClipboardList />
            )}
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

function ArmyListOptionsMenu(props) {
  const {
    event,
    player,
    setLoading,
    anchorEl,
    setAnchorEl,
    setPlayer,
    setArmyModalOpen,
  } = props;
  const open = Boolean(anchorEl);

  return (
    <StyledMenu
      id="options-menu"
      MenuListProps={{
        "aria-labelledby": "options-button",
      }}
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onClose={() => {
        setPlayer(null);
        setAnchorEl(null);
      }}
    >
      {getEnvironmentVariable("COMPANY_FLAG") !== "solforge" && (
        <MenuItem
          onClick={(e) => {
            setArmyModalOpen(true);
            setAnchorEl(null);
          }}
          disableRipple
        >
          {player?.armyListObjectId || player?.listId
            ? `Edit ${event?.gameSystem?.listLabel || "List"}`
            : `Add ${event?.gameSystem?.listLabel || "List"}`}
        </MenuItem>
      )}
      {(player?.armyListObjectId || player?.listId) && (
        <MenuItem
          href={
            player?.armyListSolforge?.id
              ? `${getEnvironmentVariable("HEADER_SOLFORGE_URL") || ""}/fused/${
                  player.armyListSolforge.id
                }`
              : `/list/${player?.armyListObjectId || player?.listId}`
          }
          target="_blank"
          component="a"
          disableRipple
        >
          View {event?.gameSystem?.listLabel || "List"}
        </MenuItem>
      )}

      <MenuItem
        onClick={async (e) => {
          if (!player.email) {
            postNotification(
              "error",
              "Player record does not have an email assigned."
            );
            return;
          }
          setLoading(true);
          try {
            await postData(`/players/${player.id}/sendListUploadRequest`);
            postNotification("success", "Upload emails have been sent");
          } catch (err) {
            postNotification("error", err);
          }
          setLoading(false);
          setPlayer(null);
          setAnchorEl(null);
        }}
        disableRipple
      >
        Send Upload Email
      </MenuItem>
    </StyledMenu>
  );
}
